"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.reversePayment = reversePayment;
const decimal_js_1 = require("decimal.js");
const numbers_1 = require("./numbers");
/**
 * Reverse-payment formula: what to charge so the organization nets `amount`
 * after the processor's cut, when tax is on the subtotal and the processing
 * share must cover the cut of the taxed total. Exactly,
 * C = amount · (1 + τ) / (1 − r · (1 + τ)); in whole cents the parts are
 * settled as a fixed point, each rounded up where produced, so the
 * organization is never short and tax is never under-collected.
 * See docs/ticketing/fees.md.
 *
 * A fixed per-transaction fee belongs to the purchase, not any one amount,
 * so it has no place here (ENG-4336).
 *
 * @param amount     cents the organization keeps before tax
 * @param rateCredit processor percentage, e.g. 2.9; zero when absorbed
 * @param taxRate    sales tax percentage, e.g. 8.5
 */
function reversePayment(amount, rateCredit, taxRate) {
    if (amount === 0) {
        return { subtotal: 0, tax: 0, processing: 0, total: 0 };
    }
    const r = new decimal_js_1.Decimal(rateCredit).div(100);
    // Tax on the subtotal, then the cut of the taxed amount. Each pass only
    // raises `processing`, by at least a cent, and the first lands within a
    // few cents of the answer, so it settles in at most 4 passes (measured:
    // never more, over every cent to $500 at 1–4.5% and 0–12% tax).
    let processing = 0;
    let settled = false;
    for (let pass = 0; pass < 6 && !settled; pass++) {
        const next = grossUp(amount + (0, numbers_1.calculateFromRate)(amount + processing, taxRate), r);
        settled = next === processing;
        processing = next;
    }
    if (!settled) {
        throw new Error(`reversePayment did not settle for ${amount} at ${rateCredit}% / ${taxRate}%`);
    }
    const subtotal = amount + processing;
    const tax = (0, numbers_1.calculateFromRate)(subtotal, taxRate);
    return { subtotal, tax, processing, total: subtotal + tax };
}
/** ⌈amount / (1 − r)⌉ − amount: the cut, rounded up. */
function grossUp(amount, r) {
    if (r.isZero()) {
        return 0;
    }
    return new decimal_js_1.Decimal(amount)
        .div(new decimal_js_1.Decimal(1).minus(r))
        .toDecimalPlaces(0, decimal_js_1.Decimal.ROUND_UP)
        .minus(amount)
        .toNumber();
}
